import { checkIsWebView } from '@camelot/webview-utils'
import { ref, watch } from 'vue'

export function useScrollIntoView(isSelected: () => boolean) {
  const elementRef = ref<HTMLElement | null>(null)

  watch(isSelected, (selected) => {
    if (selected && elementRef.value) {
      const isWebView = checkIsWebView(window)

      if (isWebView) {
        // Special handling for webview
        setTimeout(() => {
          if (elementRef.value) {
            // Use 'start' to position the element at the top of the viewport
            // This ensures it's not covered by the footer
            elementRef.value.scrollIntoView({
              behavior: 'smooth',
              block: 'center',
            })

            // Then apply an offset to move it down slightly from the very top
            // for better visibility
            const scrollParent = getScrollParent(elementRef.value)
            if (scrollParent) {
              // Add a small delay to ensure the initial scroll completes
              setTimeout(() => {
                // Move it up by 50px to prevent footer overlay
                // (negative value moves content up, positive moves it down)
                scrollParent.scrollTop -= 50
              }, 20)
            }
          }
        }, 100)
      }
      else {
        // Standard behavior for non-webview
        elementRef.value.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        })
      }
    }
  }, { immediate: true })

  return { elementRef }
}

// Helper function to find the scrollable parent of an element
function getScrollParent(element: HTMLElement): Element | null {
  if (!element)
    return null

  // Check if the element itself is scrollable
  if (element.scrollHeight > element.clientHeight) {
    return element
  }

  // Check parent elements
  return element.parentElement
    ? getScrollParent(element.parentElement)
    : document.scrollingElement || document.documentElement
}
